import { debounce, DebouncedFunc } from 'lodash';
import { io, Socket } from 'socket.io-client';
import logger from './logger';
import { TRoomKeys } from 'src/constants/rooms';
import { TOPICS } from 'src/constants/topics';
import { appStore } from 'src/mobx/appStore';
import { branchStore } from 'src/mobx/branchStore';
import { localeStore } from 'src/mobx/localesStore';
import { ordersHistoryStore } from 'src/mobx/ordersHistoryStore/ordersHistoryStore';
import { TBooleanInNumber, TCRUDEvents } from 'src/types/main';
import { TEventMessage, TOrder, TOrderCRUDEvents } from 'src/types/orders';
import { getHostName, getSocketHost } from 'src/utils/formatter';
import branchesStore from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/store/branchesStore';
import { callCenterStore } from '~/mobx/ordersStore/callCenterStore';
import { liveOrdersStore } from '~/mobx/ordersStore/liveOrdersStore';
import { CustomEvents, publish } from '~/utils/events';
import { removeToast, toastNeutral } from '~/utils/toasts';
import { cashbackStore } from '~/view/screens/MarketingScreen/screens/Cashback/store/cashbackStore';

const socketConnectionToastId: string = 'socket-connection-toast-id';

class NSocket {
  private timer: null | NodeJS.Timeout = null;
  port: number = 0;
  handlers = {
    [TOPICS.BRANCH]: this.handleBranch.bind(this),
  };

  private orderIds: { [key: string]: DebouncedFunc<(...args: any) => void> } = {};

  get domain() {
    return process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_SOCKET_CLIENT || getHostName(window.location.origin)
      : getHostName(window.location.origin);
  }

  client: null | Socket<any, any> = null;

  public subscribeToRoom(room: TRoomKeys, id: [number | string, number | string | undefined]) {
    this.client?.emit(TOPICS.ROOM, {
      id,
      room,
    });
  }

  private firstConnect = true;

  init = (authToken: string = '') => {
    try {
      if (!appStore.settings?.socket) return;
      this.firstConnect = true;

      this.runReservedRefresh();

      this.client = io(getSocketHost(), {
        extraHeaders: {
          'Accept-Language': localeStore.language,
          authorization: authToken,
          client_id: String(appStore.settings?.client_id),
          channel: window.IS_MOBILE ? 'admin-app' : 'admin-web',
        },
        path: '/sockets/socket.io',
      });

      this.client.on('connect', () => {
        if (!this.firstConnect) {
          ordersHistoryStore.getOrders();
          liveOrdersStore.refreshDataAfterSocketReconnect();
          callCenterStore.getOrders();
          publish(CustomEvents.SocketReconnect, {});
        }

        this.firstConnect = false;

        // hide unstable internet connection message on connect
        this.hideNotification();

        // get updates once if is not first connect to get updates for orders
        if (!this.firstConnect) {
          // ordersStore.runCheckingOrdersUpdates(true);
        }
        // stop checking updates on connect
        // ordersStore.stopCheckingOrdersUpdates();
      });

      this.client.on('disconnect', () => {
        // show unstable internet connection message on disconnect
        this.showNotification();
      });

      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.client.on(TOPICS.BRANCH, this.handleBranch);
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.client.on(TOPICS.CASHBACK, this.handleCashback);
      this.client.on(TOPICS.ORDER, this.debouncedHandleOrder);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          liveOrdersStore.refreshDataAfterSocketReconnect();
          this.runReservedRefresh();
        }
      });
    } catch (error: any) {
      logger.log(error);
      // ordersStore.runCheckingOrdersUpdates(false);

      // show unstable internet connection message on error
      this.showNotification();
    }
  };

  private hideNotification = () => {
    removeToast(socketConnectionToastId);
  };
  private showNotification = () => {
    toastNeutral({ id: socketConnectionToastId, title: localeStore.t('unstable_connection'), message: localeStore.t('refresh_data_list') });
  };

  private runReservedRefresh = () => {
    if (this.timer) {
      clearInterval(this.timer);
    }

    this.timer = setInterval(() => {
      liveOrdersStore.refreshDataAfterSocketReconnect();
    }, 60000);
  };

  disconnect = () => {
    if (this.timer) clearInterval(this.timer);
    this.hideNotification();
    this.client?.disconnect();
  };

  debouncedHandleOrder = (event: TEventMessage<TOrder, TOrderCRUDEvents>) => {
    if (event.data.id) {
      if (this.orderIds[event.data.id]) {
        this.orderIds[event.data.id].cancel();
      }

      this.orderIds[event.data.id] = debounce(this.handleOrder, 5000);
      this.orderIds[event.data.id](event);

      this.runReservedRefresh();
    }

    this.client?.emit('callback', { sessionId: this.client.id, messageId: event.id });
  };

  handleBranch(message: { id: string; source: string; event: TCRUDEvents; data: any }) {
    try {
      if (!message) return;
      branchStore.update(message.data);

      // show updates in branches management
      branchesStore.handleBranchUpdatesBySocket(message.data);

      this.client?.emit('callback', { sessionId: this.client.id, messageId: message.id });
    } catch (error: any) {
      logger.log(error);
    }
  }

  handleSettings(branch: any) {
    try {
      if (!branch || !branch) return;
    } catch (error: any) {
      logger.log(error);
    }
  }

  handleOrder = (message: TEventMessage<TOrder, TOrderCRUDEvents>) => {
    try {
      delete this.orderIds[message.data.id];
      if (!message) return;
      ordersHistoryStore.handleSocketEvent(message);
      liveOrdersStore.handleUpdateOrder(message.data, message.event);
      callCenterStore.handleUpdateOrder(message.data, message.event);

      publish(CustomEvents.SocketUpdate, message);
    } catch (error: any) {
      logger.log(error);
    }
  };

  handleCashback(data: { id: number; state: TBooleanInNumber }) {
    cashbackStore.handleCashbackUpdateBySocket(data.id, data.state);
  }
}
const socket = new NSocket();

export default socket;
